import React, { useState, useEffect, useMemo, useRef } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { getAllCustomerDetails, CustomerData, CustomerDetailsResponse, PaginationInfo } from "../../services/api/user.service";
import { toast } from "react-toastify";
import * as XLSX from "xlsx";
import { Eye, Filter, X, ChevronDown, FileSpreadsheet, FileText, ChevronLeft, ChevronRight } from "lucide-react";

// Separate Table Loader Component
const TableLoader: React.FC = () => {
  return (
    <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center z-10 backdrop-blur-sm">
      <div className="flex flex-col items-center gap-3">
        <div className="w-8 h-8 border-4 border-gray-300 border-t-gray-800 rounded-full animate-spin"></div>
        <span className="text-sm text-gray-600 font-family-messina-sans">Loading...</span>
      </div>
    </div>
  );
};

const CustomerDetails: React.FC = () => {
  const navigate = useNavigate();
  const [customerData, setCustomerData] = useState<CustomerData[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [tableLoading, setTableLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [itemsPerPage, setItemsPerPage] = useState<number>(10);
  const [pagination, setPagination] = useState<PaginationInfo | null>(null);
  
  // Filter states (for input values)
  const [filters, setFilters] = useState({
    artistName: "",
    accountNumber: "",
    fullName: "",
  });
  // Debounced filter values (used for API calls)
  const [debouncedFilters, setDebouncedFilters] = useState({
    artistName: "",
    accountNumber: "",
    fullName: "",
  });
  const [showFilters, setShowFilters] = useState<boolean>(false);
  const [showExportMenu, setShowExportMenu] = useState<boolean>(false);

  const isInitialLoad = useRef(true);

  // Get systemRole from sessionStorage and check if user has access
  const getSystemRole = (): string | null => {
    try {
      return sessionStorage.getItem('systemRole');
    } catch {
      return null;
    }
  };

  const systemRole = getSystemRole();

  // Redirect to dashboard if not system admin
  if (systemRole !== "system_admin") {
    return <Navigate to="/dashboard" replace />;
  }

  // Debounce filter values (500ms delay)
  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      setDebouncedFilters(filters);
    }, 500);

    return () => {
      clearTimeout(debounceTimer);
    };
  }, [filters]);

  // Transform nested API response to flattened table data
  const transformCustomerData = (data: CustomerDetailsResponse[]): CustomerData[] => {
    const flattened: CustomerData[] = [];

    if (!data || !Array.isArray(data)) {
      return flattened;
    }

    data.forEach((customer) => {
      // Get artist name from array, use first one or empty string
      const artistName = customer.artist_name && Array.isArray(customer.artist_name) && customer.artist_name.length > 0 
        ? customer.artist_name[0] 
        : "";

      // Create one row per account
      if (customer.accounts && Array.isArray(customer.accounts) && customer.accounts.length > 0) {
        customer.accounts.forEach((account) => {
          flattened.push({
            user_id: customer.user_id,
            artist_name: artistName,
            firstname: customer.firstname ?? null,
            lastname: customer.lastname ?? null,
            username: customer.username ?? null,
            account_number: account.account_number || "N/A",
            account_status: account.account_status || "N/A",
            account_created_at: account.account_created_at || null,
            routing_number: account.routing_number || "N/A",
            accounts: [account],
            // Keep customer_id for navigation compatibility (using user_id)
            customer_id: customer.user_id
          });
        });
      } else {
        // If no accounts, still create a row with user info
        flattened.push({
          user_id: customer.user_id,
          artist_name: artistName,
          firstname: customer.firstname ?? null,
          lastname: customer.lastname ?? null,
          username: customer.username ?? null,
          account_number: "N/A",
          account_status: "N/A",
          account_created_at: null,
          routing_number: "N/A",
          accounts: [],
          customer_id: customer.user_id
        });
      }
    });

    return flattened;
  };

  useEffect(() => {
    const fetchCustomerDetails = async () => {
      try {
        // Use tableLoading only if it's not the initial load
        if (isInitialLoad.current) {
          setLoading(true);
        } else {
          setTableLoading(true);
        }
        setError(null);
        
        // Prepare filter parameters for API using debounced filters
        const filterParams: {
          artist_name?: string;
          account_number?: string;
          fullName?: string;
          firstname?: string;
          lastname?: string;
        } = {};
        if (debouncedFilters.artistName) filterParams.artist_name = debouncedFilters.artistName;
        if (debouncedFilters.accountNumber) filterParams.account_number = debouncedFilters.accountNumber;

        const trimmedFullName = debouncedFilters.fullName?.trim() || "";
        if (trimmedFullName) {
          const tokens = trimmedFullName.split(/\s+/).filter(Boolean);
          if (tokens.length === 1) {
            filterParams.fullName = tokens[0];
          } else {
            // Multiple tokens: match firstname + lastname together
            filterParams.firstname = tokens[0];
            filterParams.lastname = tokens.slice(1).join(" ");
          }
        }
        
        const response = await getAllCustomerDetails(
          currentPage,
          itemsPerPage,
          false,
          Object.keys(filterParams).length > 0 ? filterParams : undefined
        );
        
        const transformed = transformCustomerData(response.data);
        setCustomerData(transformed);
        setPagination(response.pagination);
      } catch (err: any) {
        console.error("Error fetching customer details:", err);
        setError(err.message || "Failed to fetch customer details");
        toast.error(err.message || "Failed to fetch customer details");
      } finally {
        if (isInitialLoad.current) {
          setLoading(false);
          isInitialLoad.current = false;
        } else {
          setTableLoading(false);
        }
      }
    };

    fetchCustomerDetails();
  }, [currentPage, itemsPerPage, debouncedFilters.artistName, debouncedFilters.accountNumber, debouncedFilters.fullName]);

  // Close export menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (showExportMenu && !target.closest('.export-menu-container')) {
        setShowExportMenu(false);
      }
    };

    if (showExportMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showExportMenu]);

  // Format date to show only the date in MM/DD/YYYY format
  const formatDate = (dateString: string): string => {
    if (!dateString) return "N/A";
    try {
      const date = new Date(dateString);
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const year = date.getFullYear();
      return `${month}/${day}/${year}`;
    } catch {
      return "N/A";
    }
  };

  const formatCurrency = (amount: number | string, currency: string) => {
    const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency || 'USD'
    }).format(numAmount);
  };

  // Use server-side pagination info
  const totalPages = pagination?.totalPages || 0;
  const currentPageData = customerData;

  // Reset to page 1 when debounced filters change (will trigger new fetch)
  useEffect(() => {
    if (!isInitialLoad.current) {
      setCurrentPage(1);
    }
  }, [debouncedFilters.artistName, debouncedFilters.accountNumber, debouncedFilters.fullName]);

  const handlePageChange = (page: number) => {
    if (pagination && page >= 1 && page <= pagination.totalPages) {
      setCurrentPage(page);
      // Scroll to top of table
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const getPageNumbers = () => {
    const pages: (number | string)[] = [];
    const maxVisiblePages = 5;

    if (totalPages === 0) {
      // Show page 1 even when there are no records
      pages.push(1);
      return pages;
    }

    if (totalPages <= maxVisiblePages) {
      // Show all pages if total pages is less than max visible
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Show first page
      pages.push(1);

      // Calculate start and end of visible pages
      let start = Math.max(2, currentPage - 1);
      let end = Math.min(totalPages - 1, currentPage + 1);

      // Adjust if we're near the start
      if (currentPage <= 3) {
        start = 2;
        end = 4;
      }

      // Adjust if we're near the end
      if (currentPage >= totalPages - 2) {
        start = totalPages - 3;
        end = totalPages - 1;
      }

      // Add ellipsis after first page if needed
      if (start > 2) {
        pages.push('...');
      }

      // Add visible pages
      for (let i = start; i <= end; i++) {
        pages.push(i);
      }

      // Add ellipsis before last page if needed
      if (end < totalPages - 1) {
        pages.push('...');
      }

      // Show last page
      pages.push(totalPages);
    }

    return pages;
  };

  // Fetch all data based on filters (ignoring pagination)
  const fetchAllFilteredData = async (): Promise<CustomerData[]> => {
    try {
      // Prepare filter parameters for API using debounced filters
      const filterParams: {
        artist_name?: string;
        account_number?: string;
        fullName?: string;
        firstname?: string;
        lastname?: string;
      } = {};
      if (debouncedFilters.artistName) filterParams.artist_name = debouncedFilters.artistName;
      if (debouncedFilters.accountNumber) filterParams.account_number = debouncedFilters.accountNumber;

      const trimmedFullName = debouncedFilters.fullName?.trim() || "";
      if (trimmedFullName) {
        const tokens = trimmedFullName.split(/\s+/).filter(Boolean);
        if (tokens.length === 1) {
          filterParams.fullName = tokens[0];
        } else {
          filterParams.firstname = tokens[0];
          filterParams.lastname = tokens.slice(1).join(" ");
        }
      }
      
      // Single API call with export=true to get all filtered data
      const response = await getAllCustomerDetails(1, 1, true, Object.keys(filterParams).length > 0 ? filterParams : undefined);
      return transformCustomerData(response.data);
    } catch (error) {
      console.error("Error fetching all data for export:", error);
      throw error;
    }
  };

  // Prepare export data (shared between CSV and Excel)
  const prepareExportData = async () => {
    // Fetch all filtered data (ignoring pagination)
    const allFilteredData = await fetchAllFilteredData();
    
    return allFilteredData.map((customer) => ({
      "Artist name": customer.artist_name || "",
      "Full Name": [customer.firstname, customer.lastname].filter(Boolean).join(' ') || "N/A",
      "Account Number": customer.account_number || "N/A",
      "Routing Number": customer.routing_number || "N/A",
      "Account Status": customer.account_status || ""
    }));
  };

  const exportToExcel = async () => {
    try {
      toast.info("Preparing export data...");
      const exportData = await prepareExportData();
      
      if (exportData.length === 0) {
        toast.error("No data to export");
        return;
      }

      // Create a new workbook and worksheet
      const worksheet = XLSX.utils.json_to_sheet(exportData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Customer Details");

      // Generate Excel file
      const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
      const blob = new Blob([excelBuffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      
      // Generate filename with current date
      const date = new Date();
      const dateStr = date.toISOString().split("T")[0];
      link.download = `customer_details_${dateStr}.xlsx`;
      
      // Trigger download
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      setShowExportMenu(false);
      toast.success("Excel file downloaded successfully");
    } catch (error) {
      console.error("Error exporting to Excel:", error);
      toast.error("Failed to export Excel file");
    }
  };

  const exportToCSV = async () => {
    try {
      toast.info("Preparing export data...");
      const exportData = await prepareExportData();
      
      if (exportData.length === 0) {
        toast.error("No data to export");
        return;
      }

      // Convert to CSV format
      const headers = Object.keys(exportData[0]);
      const csvRows = [
        headers.join(','), // Header row
        ...exportData.map(row => 
          headers.map(header => {
            const value = row[header as keyof typeof row];
            // Escape commas and quotes in CSV
            if (value === null || value === undefined) return '';
            const stringValue = String(value);
            if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')) {
              return `"${stringValue.replace(/"/g, '""')}"`;
            }
            return stringValue;
          }).join(',')
        )
      ];

      const csvContent = csvRows.join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      
      // Generate filename with current date
      const date = new Date();
      const dateStr = date.toISOString().split("T")[0];
      link.download = `customer_details_${dateStr}.csv`;
      
      // Trigger download
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      setShowExportMenu(false);
      toast.success("CSV file downloaded successfully");
    } catch (error) {
      console.error("Error exporting to CSV:", error);
      toast.error("Failed to export CSV file");
    }
  };

  if (loading) {
    return (
      <div className="bg-nova-dashboard-900 min-h-full md:min-h-screen">
        <div className="p-4 sm:p-6 md:p-8">
          <span className="text-[1.875rem] font-regular text-gray-900 block font-family-messina-modern">Customer Details</span>
          <div className="flex items-center justify-center mt-[5rem]">
            <div className="text-gray-600 font-family-messina-sans">Loading...</div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-nova-dashboard-900 min-h-full md:min-h-screen">
        <div className="p-4 sm:p-6 md:p-8">
          <span className="text-[1.875rem] font-regular text-gray-900 block font-family-messina-modern">Customer Details</span>
          <div className="flex items-center justify-center mt-[5rem]">
            <div className="text-red-600 font-family-messina-sans">{error}</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-nova-dashboard-900 min-h-full md:min-h-screen">
      {/* Main Content Area */}
      <div className="p-4 sm:p-6 md:p-8">
        {/* Header (desktop sticky, mobile static) */}
        <div className="sticky top-0 z-30 isolate -mx-4 sm:-mx-6 md:-mx-8 px-4 sm:px-6 md:px-8 py-4 mb-4 border-b border-gray-300 bg-[#F1F0EC] opacity-100 shadow-[0_1px_3px_rgba(0,0,0,0.06)] flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
          <span className="text-[1.875rem] leading-[1.1] font-regular text-gray-900 block font-family-messina-modern">Accounts Information</span>
          <div className="w-full sm:w-auto flex items-center flex-wrap gap-2 sm:gap-3">
            <button
              onClick={() => navigate('/account-transactions-summary')}
              className="px-3 sm:px-4 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-700 transition-colors text-sm font-family-messina-sans"
            >
              Summary
            </button>
            {customerData.length > 0 && (
              <div className="relative export-menu-container">
                <button
                  onClick={() => setShowExportMenu(!showExportMenu)}
                  className="px-3 sm:px-4 py-2 bg-gray-800 text-white rounded-lg hover:bg-gray-700 transition-colors text-sm font-family-messina-sans flex items-center gap-2"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <span>Export</span>
                  <ChevronDown size={16} />
                </button>
                {showExportMenu && (
                  <div className="absolute left-0 sm:left-auto sm:right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 z-10">
                    <button
                      onClick={exportToExcel}
                      className="w-full text-left px-4 py-3 hover:bg-gray-100 transition-colors text-sm font-family-messina-sans flex items-center gap-2 text-gray-900"
                    >
                      <FileSpreadsheet size={16} />
                      <span>Export as Excel</span>
                    </button>
                    <button
                      onClick={exportToCSV}
                      className="w-full text-left px-4 py-3 hover:bg-gray-100 transition-colors text-sm font-family-messina-sans flex items-center gap-2 text-gray-900 border-t border-gray-200"
                    >
                      <FileText size={16} />
                      <span>Export as CSV</span>
                    </button>
                  </div>
                )}
              </div>
            )}
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`flex items-center gap-2 px-3 sm:px-4 py-2 rounded-lg transition-colors text-sm font-family-messina-sans ${
                showFilters || filters.artistName || filters.accountNumber || filters.fullName
                  ? "bg-gray-800 text-white hover:bg-gray-700"
                  : "bg-gray-200 text-gray-700 hover:bg-gray-300"
              }`}
            >
              <Filter size={16} />
              <span>Filter</span>
              {(filters.artistName || filters.accountNumber || filters.fullName) && (
                <span className="ml-1 px-1.5 py-0.5 bg-white text-gray-800 rounded-full text-xs font-medium">
                  {[filters.artistName, filters.accountNumber, filters.fullName].filter(Boolean).length}
                </span>
              )}
            </button>
          </div>
        </div>

        {/* Filter Section - Toggleable */}
        {showFilters && (
          <div className="mb-6 bg-white rounded-lg p-4 shadow-sm">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-[1.125rem] font-regular text-gray-900 font-family-messina-modern">Filters</h3>
              <button
                onClick={() => setShowFilters(false)}
                className="text-gray-500 hover:text-gray-700 transition-colors"
                aria-label="Close filters"
              >
                <X size={20} />
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Artist Name Filter */}
              <div>
                <label className="block text-[0.625rem] font-medium text-gray-500 uppercase tracking-wide mb-2 sidebar-menu-item">
                  ARTIST NAME
                </label>
                <input
                  type="text"
                  value={filters.artistName}
                  onChange={(e) => setFilters(prev => ({ ...prev, artistName: e.target.value }))}
                  placeholder="Filter by Artist Name"
                  className="w-full text-[0.875rem] text-gray-900 bg-transparent border-b border-gray-300 focus:outline-none focus:border-gray-900 pb-2 font-family-messina-sans"
                />
              </div>

              {/* Account Number Filter */}
              <div>
                <label className="block text-[0.625rem] font-medium text-gray-500 uppercase tracking-wide mb-2 sidebar-menu-item">
                  ACCOUNT NUMBER
                </label>
                <input
                  type="text"
                  value={filters.accountNumber}
                  onChange={(e) => setFilters(prev => ({ ...prev, accountNumber: e.target.value }))}
                  placeholder="Filter by Account Number"
                  className="w-full text-[0.875rem] text-gray-900 bg-transparent border-b border-gray-300 focus:outline-none focus:border-gray-900 pb-2 font-family-messina-sans"
                />
              </div>

              {/* Full Name Filter */}
              <div>
                <label className="block text-[0.625rem] font-medium text-gray-500 uppercase tracking-wide mb-2 sidebar-menu-item">
                  FULL NAME
                </label>
                <input
                  type="text"
                  value={filters.fullName}
                  onChange={(e) => setFilters(prev => ({ ...prev, fullName: e.target.value }))}
                  placeholder="Filter by Full Name"
                  className="w-full text-[0.875rem] text-gray-900 bg-transparent border-b border-gray-300 focus:outline-none focus:border-gray-900 pb-2 font-family-messina-sans"
                />
              </div>
            </div>

            {/* Clear Filters Button */}
            {(filters.artistName || filters.accountNumber || filters.fullName) && (
              <div className="mt-4">
                <button
                  onClick={() => setFilters({ artistName: "", accountNumber: "", fullName: "" })}
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors text-sm font-family-messina-sans"
                >
                  Clear Filters
                </button>
              </div>
            )}
          </div>
        )}

        {/* Customer Details Table */}
        <div className="mb-6 sm:mb-8 mt-[2rem]">
          {/* <span className="text-[1.4375rem] font-regular text-gray-900 mb-6 block font-family-messina-modern">Customer </span> */}
          
          {customerData.length === 0 ? (
            <div className="text-center py-8">
              <div className="text-gray-600 font-family-messina-sans">No customer data available</div>
            </div>
          ) : (
            <>
            <div className="relative overflow-x-auto min-h-[200px]">
              {tableLoading && <TableLoader />}
            <table className="w-full border-collapse">
              <thead>
                <tr className="border-b border-gray-300">
                  <th className="text-left py-3 px-4 text-[0.625rem] font-medium text-gray-500 uppercase tracking-wide sidebar-menu-item">Artist name</th>
                      <th className="text-left py-3 px-4 text-[0.625rem] font-medium text-gray-500 uppercase tracking-wide sidebar-menu-item">Full Name</th>
                  <th className="text-left py-3 px-4 text-[0.625rem] font-medium text-gray-500 uppercase tracking-wide sidebar-menu-item">Account Number</th>
                  <th className="text-left py-3 px-4 text-[0.625rem] font-medium text-gray-500 uppercase tracking-wide sidebar-menu-item">Routing Number</th>
                  <th className="text-left py-3 px-4 text-[0.625rem] font-medium text-gray-500 uppercase tracking-wide sidebar-menu-item">Account Status</th>
                  <th className="text-left py-3 px-4 text-[0.625rem] font-medium text-gray-500 uppercase tracking-wide sidebar-menu-item">Actions</th>
                  <th className="text-left py-3 px-4 text-[0.625rem] font-medium text-gray-500 uppercase tracking-wide sidebar-menu-item">Transactions</th>
                </tr>
              </thead>
              <tbody>
                {currentPageData.map((customer, index) => {
                  const accountId = customer.accounts?.[0]?.account_id;
                  return (
                  <tr key={`${customer.user_id}-${customer.account_number}-${index}`} className="border-b border-gray-200 hover:bg-gray-50">
                    <td className="py-3 px-4 text-[0.875rem] text-gray-900 font-family-messina-sans">{customer.artist_name || "N/A"}</td>
                    <td className="py-3 px-4 text-[0.875rem] text-gray-900 font-family-messina-sans">
                      {[customer.firstname, customer.lastname].filter(Boolean).join(' ') || 'N/A'}
                    </td>
                    <td className="py-3 px-4 text-[0.875rem] text-gray-900 font-family-messina-sans">{customer.account_number || "N/A"}</td>
                    <td className="py-3 px-4 text-[0.875rem] text-gray-900 font-family-messina-sans">{customer.routing_number || "N/A"}</td>
                    <td className="py-3 px-4 text-[0.875rem] text-gray-900 font-family-messina-sans">
                      <span className={`inline-block px-2 py-1 rounded text-xs ${
                        customer.account_status === "Open" ? "bg-green-100 text-green-800" : 
                        customer.account_status === "Closed" ? "bg-red-100 text-red-800" : 
                        "bg-gray-100 text-gray-800"
                      }`}>
                        {customer.account_status}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-[0.875rem] text-gray-900 font-family-messina-sans">
                      <button
                        onClick={() => navigate(`/customer-profile/${customer.customer_id || customer.user_id}`, { state: { customerId: customer.customer_id || customer.user_id, userId: customer.user_id } })}
                        className="flex items-center gap-1 text-gray-700 hover:text-gray-900 transition-colors"
                        title="View more details"
                      >
                        <Eye className="h-4 w-4" />
                        <span className="text-xs">View more</span>
                      </button>
                    </td>
                    <td className="py-3 px-4 text-[0.875rem] text-gray-900 font-family-messina-sans">
                      <button
                        onClick={() => {
                          if (!accountId) return;
                          navigate(`/account-transactions/${accountId}`, {
                            state: {
                              accountContext: {
                                accountId,
                                artistName: customer.artist_name || null,
                                firstname: customer.firstname || null,
                                lastname: customer.lastname || null,
                                accountNumber: customer.account_number && customer.account_number !== 'N/A'
                                  ? customer.account_number
                                  : null,
                                accountStatus: customer.account_status && customer.account_status !== 'N/A'
                                  ? customer.account_status
                                  : null,
                              },
                            },
                          });
                        }}
                        disabled={!accountId}
                        className="flex items-center gap-1 text-gray-700 hover:text-gray-900 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        title="View transactions"
                      >
                        <Eye size={16} />
                      </button>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          
          {/* Pagination Controls */}
          {totalPages > 1 && (
          <div className="mt-6 flex flex-col sm:flex-row items-center justify-between gap-4">
            {/* Items per page selector */}
            <div className="flex items-center gap-2">
              <span className="text-[0.875rem] text-gray-600 font-family-messina-sans">Items per page:</span>
              <select
                value={itemsPerPage}
                onChange={(e) => {
                  setItemsPerPage(Number(e.target.value));
                  setCurrentPage(1);
                }}
                className="border border-gray-300 rounded px-2 py-1 text-[0.875rem] text-gray-900 font-family-messina-sans focus:outline-none focus:border-gray-900"
              >
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
            </div>

            {/* Page info */}
            <div className="text-[0.875rem] text-gray-600 font-family-messina-sans">
              {pagination ? (
                <>
                  Showing {((pagination.page - 1) * pagination.limit) + 1} to {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} records
                </>
              ) : (
                <>
                  Showing {currentPageData.length} records
                </>
              )}
            </div>

            {/* Pagination buttons */}
            <div className="flex items-center gap-2">
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={!pagination?.hasPrevPage}
                className={`p-2 rounded border ${
                  !pagination?.hasPrevPage
                    ? "border-gray-300 text-gray-400 cursor-not-allowed"
                    : "border-gray-300 text-gray-700 hover:bg-gray-100"
                } transition-colors`}
                aria-label="Previous page"
              >
                <ChevronLeft size={16} />
              </button>

              {/* Page numbers */}
              <div className="flex items-center gap-1">
                {getPageNumbers().map((page, index) => {
                  if (page === '...') {
                    return (
                      <span key={`ellipsis-${index}`} className="px-2 text-gray-400">
                        ...
                      </span>
                    );
                  }

                  const pageNum = page as number;
                  return (
                    <button
                      key={pageNum}
                      onClick={() => handlePageChange(pageNum)}
                      className={`px-3 py-1 rounded text-[0.875rem] font-family-messina-sans transition-colors ${
                        currentPage === pageNum
                          ? "bg-gray-800 text-white"
                          : "border border-gray-300 text-gray-700 hover:bg-gray-100"
                      }`}
                    >
                      {pageNum}
                    </button>
                  );
                })}
              </div>

              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={!pagination?.hasNextPage}
                className={`p-2 rounded border ${
                  !pagination?.hasNextPage
                    ? "border-gray-300 text-gray-400 cursor-not-allowed"
                    : "border-gray-300 text-gray-700 hover:bg-gray-100"
                } transition-colors`}
                aria-label="Next page"
              >
                <ChevronRight size={16} />
              </button>
            </div>
          </div>
          )}
          </>
          )}

          {/* Additional Details Section - Expandable rows could be added here */}
          {/* {customerData.length > 0 && (
          <div className="mt-8">
            <span className="text-[1.4375rem] font-regular text-gray-900 mb-4 block font-family-messina-modern">Detailed Information</span>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {customerData.map((customer, index) => (
                <div key={`${customer.customer_id}-${customer.account_id}-${index}`} className="bg-white border border-gray-200 rounded-lg p-4">
                  <h3 className="text-sm font-semibold text-gray-900 mb-3 font-family-messina-modern">
                    {customer.account_id ? `Account ${customer.account_id}` : `Record #${index + 1}`}
                  </h3>
                  <div className="space-y-2 text-xs">
                    <div className="flex justify-between">
                      <span className="text-gray-500 sidebar-menu-item">Customer Type:</span>
                      <span className="text-gray-900 font-family-messina-sans">{customer.customer_type}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500 sidebar-menu-item">Address:</span>
                      <span className="text-gray-900 font-family-messina-sans">{customer.address}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500 sidebar-menu-item">Routing Number:</span>
                      <span className="text-gray-900 font-family-messina-sans">{customer.routing_number || "N/A"}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500 sidebar-menu-item">Hold Amount:</span>
                      <span className="text-gray-900 font-family-messina-sans">{formatCurrency(customer.hold, customer.currency)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500 sidebar-menu-item">Application ID:</span>
                      <span className="text-gray-900 font-family-messina-sans">{customer.application_id}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500 sidebar-menu-item">Account Created:</span>
                      <span className="text-gray-900 font-family-messina-sans">{formatDate(customer.account_created_at)}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
          )} */}
        </div>
      </div>
    </div>
  );
};

export default CustomerDetails;

